import { UpdateWidget } from 'modules/Editor/store/infographSlice.types';
import { PageSize, WidgetsMap } from 'types/infographTypes';
import { WidgetType } from 'types/widget.types';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { LineWidgetData } from 'widgets/LineWidget/LineWidget.types';
import { rescaleCells } from 'widgets/TextBasedWidgets/TableWidget/TableWidget.helpers';
import { TableWidgetData } from 'widgets/TextBasedWidgets/TableWidget/TableWidget.types';
import { TextWidgetData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';
import { modifyAllFontSizes } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.helpers';
import { TextBasedWidgetData } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData, WidgetMap } from 'widgets/Widget.types';

import { THUMBNAIL_OVERLAY_BTN_CLASS } from './TemplatesMenu.config';
import { TemplateSize } from './TemplatesMenu.types';

/**
 * Helper fn to check if the page number in a given template is currently being loaded + added to canvas
 *
 * @param loadingTemplateId template currently being loaded with format `<TEMPLATE_ID>/<PAGE_NUMBER>` - page_number is optional
 * @param currentTemplateId Current template
 * @param pageNumber Page number to check
 * @returns boolean
 */
export const isLoadingPage = (loadingTemplateId: string | null, currentTemplateId: string, pageNumber: number) => {
  if (!loadingTemplateId) return false;

  const [templateId, loadingPageNum] = loadingTemplateId.split('/');
  return templateId === currentTemplateId && loadingPageNum === `${pageNumber}`;
};

/**
 * Blur the thumbnail overlay button if it is active
 */
export const blurTemplateOverlayButtonIfActive = () => {
  const activeElement = document.activeElement as HTMLElement;
  if (activeElement?.classList.contains(THUMBNAIL_OVERLAY_BTN_CLASS)) {
    activeElement.blur();
  }
};

/**
 * Get aspect ratio between existing page and newly added page.
 *
 * @param size Current Infograph page size.
 * @param templateSize Newly added template size.
 * @returns {[number, number]}
 */
export const getXYRatio = (size: PageSize, templateSize: TemplateSize): [number, number] => {
  let xRatio = 1.0;
  let yRatio = 1.0;

  if (templateSize.widthPx > size.widthPx) {
    xRatio = templateSize.widthPx / size.widthPx;
  }

  if (templateSize.heightPx > size.heightPx) {
    yRatio = templateSize.heightPx / size.heightPx;
  }

  return [xRatio, yRatio];
};

/**
 * Rescale all widgets in params.
 *
 * @param pageWidgets Widgets in a particular page.
 * @param size Infograph
 * @param templateSize TemplateSize
 * @returns {UpdateWidget[]}
 */
export const rescaleTemplates = (
  pageWidgets: WidgetMap,
  size: PageSize,
  templateSize: TemplateSize,
): UpdateWidget[] | undefined => {
  let left: number;
  let top: number;
  let width: number | undefined;
  let height: number | undefined;
  let batchWidgetUpdate: UpdateWidget[] = [];
  const [xRatio] = getXYRatio(size, templateSize);

  // only rescale when new template/page width is larger than existing template
  if (xRatio < 1.0) {
    return;
  }

  Object.keys(pageWidgets).forEach((id) => {
    const currentWidget = pageWidgets[id];
    const widgetType = getWidgetTypeFromId(id);
    left = currentWidget.leftPx / xRatio;
    top = currentWidget.topPx / xRatio;
    width = currentWidget.widthPx / xRatio;
    height = currentWidget.heightPx && currentWidget.heightPx / xRatio;

    switch (widgetType) {
      case WidgetType.Text:
        const textWidgetData = currentWidget as TextBasedWidgetData;
        if (textWidgetData.proseMirrorData) {
          const newContent = modifyAllFontSizes(textWidgetData.proseMirrorData, 1 / xRatio, { round: true });
          batchWidgetUpdate.push({
            widgetId: id,
            widgetData: {
              ...textWidgetData,
              leftPx: left,
              topPx: top,
              widthPx: width,
              proseMirrorData: newContent,
            } as TextWidgetData,
          });
        }
        break;
      case WidgetType.Table:
        const tableWidgetData = currentWidget as TextBasedWidgetData;
        if (tableWidgetData.proseMirrorData) {
          const newContent = modifyAllFontSizes(rescaleCells(tableWidgetData.proseMirrorData, 1 / xRatio), 1 / xRatio, {
            round: true,
          });
          batchWidgetUpdate.push({
            widgetId: id,
            widgetData: {
              ...tableWidgetData,
              leftPx: left,
              topPx: top,
              widthPx: width,
              proseMirrorData: newContent,
            } as TableWidgetData,
          });
        }
        break;
      case WidgetType.Image:
        const imageWidgetData = currentWidget as ImageWidgetData;
        batchWidgetUpdate.push({
          widgetId: id,
          widgetData: {
            ...imageWidgetData,
            leftPx: left,
            topPx: top,
            widthPx: width,
            heightPx: height,
            imageRect: {
              widthPx: imageWidgetData.imageRect.widthPx / xRatio,
              heightPx: imageWidgetData.imageRect.heightPx / xRatio,
            },
            crop: {
              top: imageWidgetData.crop.top / xRatio,
              bottom: imageWidgetData.crop.bottom / xRatio,
              left: imageWidgetData.crop.left / xRatio,
              right: imageWidgetData.crop.right / xRatio,
            },
          } as ImageWidgetData,
        });
        break;
      case WidgetType.Line:
        const lineWidgetData = currentWidget as LineWidgetData;
        batchWidgetUpdate.push({
          widgetId: id,
          widgetData: {
            ...lineWidgetData,
            leftPx: left,
            topPx: top,
            widthPx: width,
            heightPx: height,
            startPos: {
              xPx: lineWidgetData.startPos.xPx / xRatio,
              yPx: lineWidgetData.startPos.yPx / xRatio,
            },
            endPos: {
              xPx: lineWidgetData.endPos.xPx / xRatio,
              yPx: lineWidgetData.endPos.yPx / xRatio,
            },
          },
        });
        break;
      default:
        batchWidgetUpdate.push({
          widgetId: id,
          widgetData: {
            ...currentWidget,
            leftPx: left,
            topPx: top,
            widthPx: width,
            heightPx: height,
          },
        });
        break;
    }
  });

  return batchWidgetUpdate;
};

/**
 * Scale widget position and size, then returns the WidgetsMap object.
 *
 * @param widgets Widgets to be scaled
 * @param param1 xRatio, yRatio
 * @param param2 paddingTop, paddingLeft
 * @returns {WidgetsMap}
 */
export const rescaleAndMapTemplates = (
  pageWidgets: WidgetMap,
  size: PageSize,
  templateSize: TemplateSize,
): WidgetsMap => {
  const rescaledWidgets: WidgetsMap = {};
  const batchWidgetUpdate = rescaleTemplates(pageWidgets, size, templateSize);
  batchWidgetUpdate?.forEach((item) => {
    rescaledWidgets[item.widgetId] = item.widgetData as AllWidgetData;
  });

  return rescaledWidgets;
};
